import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { addYears } from 'date-fns';
import styled from 'styled-components';
import { AuthUtils, CostCenter, FlowjoCommons, Product } from '@portal/portal-core';

import type { AppDispatch } from 'store';
import { useAppDispatch } from 'store/hooks';

import {
  RELEASE,
  BETA,
  TRIAL,
  TRAINING,
  PENDING,
  ACTIVE,
  CANCELLED,
  SUSPENDED,
} from 'constants/filters';
import { CloudAdminActions } from 'actions';
import { checkCostCenterName } from 'ducks/super-client/actions';
import { modalsSelector } from 'selectors';
import { Wizard, Input, inputSizes } from 'components/styled-primitives';

import { LayoutContext } from 'components/layout/layout-context';
import { FjFlexContainer, fjModalSizes } from 'components/primitives';
import { SeatSummary, SeatUsage } from 'components/licenses/seat-summary';
import { PRODUCT_INFO } from 'constants/products';
import {
  selectActiveTool,
  selectCostCenterNameCheck,
  selectCurrentLicense,
  selectSeatUsageForProductAccess,
  selectActiveProductAccess,
} from 'selectors/super-admin';
import { MODAL_TYPES, TOOL_BUTTON_NAMES } from 'constants/super-admin';

import {
  composeValidators,
  convertToStringMatchRelaxed,
  didLicensePropertyChange,
  formatDate,
  formatEnabledFeatures,
  prettyPrintArray,
  parseDate,
  prefixEmailDomain,
} from 'utils';

import {
  validateFirstName,
  validateLastName,
  validateAddress,
  validateUSZipCode,
  validateSeatCount,
  validatePhone,
  validateID,
  validateCostCenterName,
} from 'utils/validators';
import type { CostCenterNameCheckState } from 'ducks/super-client/reducers/cost-center-name-check';
import type { LicenseData } from './modal-utils';
import {
  getExistingMatch,
  hasMatchingProductNameAndProductAccessType,
  validateDuplicateCostCenterName,
  costCenterDuplicateValidationMessage,
} from './modal-utils';

import { buildCreateLicenseAction, buildCreateUserAndLicenseAction } from '../actions/create';
import { buildManageLicenseAction } from '../actions/manage';
import fetchUsersByIds from '../actions/fetch-users-by-ids';
import ModalInputSection from '../components/modal-input-section';
import ModalRadioGroup from '../components/modal-radio-group';
import { WizardInnerPane } from '../components';

import {
  SharedMaxSeatStorageManager,
  AuthorizedDevicesOverride,
  BillingPeriodManager,
  EmailSuffixManager,
  SeatStorageManager,
  CoAdminList,
  CountryCode,
  EmailLookup,
  ProductSelector,
  ProductFeaturesManager,
} from './components';

// A single product feature entry as stored on a license's product access.
interface ProductFeature {
  productFeatureId?: string;
  enabled?: boolean;
  valueType?: string;
  value?: string | number | boolean | Date | null;
  name?: string;
}

// A co-administrator user record returned from the users-by-id fetch.
interface AdminUser {
  id: string;
  primaryEmail?: string;
  firstname?: string;
  lastname?: string;
  countryCode?: string;
}

// Loose shape of a typeahead user match selected in the admin lookup.
interface AdminTypeaheadMatch {
  id?: string;
  primaryEmail?: string;
  firstname?: string;
  lastname?: string;
  countryCode?: string;
}

// A license/activity radio option (a `FilterOption` at runtime; `ModalRadioGroup`
// only types the minimal `{ id, label }` shape, so widen it back here).
interface LicenseRadioOption {
  id: string | number;
  label: string;
  productAccessType?: string;
  activityState?: string;
}

// Minimal accessed shape of the currently-selected license (untyped redux state).
interface SelectedLicense {
  admin: {
    firstname: string;
    lastname: string;
    primaryEmail: string;
  };
  costCenter: {
    id: string;
    name: string;
    countryCode: string;
    whitelistDomains: string[];
    whitelistIgnore: boolean;
    admins: string[];
  };
  productAccess: {
    id: string;
    name: string;
    activityState: string;
    type: string;
    startDate: string;
    endDate: string;
    maxActiveUsers: number;
    maxSharedComputers: number;
    region: string;
    owner: string;
    authorizedDevicesOverride: boolean;
    features: Record<string, ProductFeature>;
  };
}

// The consolidated modal form state (mirrors the former class `state`).
interface LicenseModalState {
  productName: string;
  activityState: string | undefined;
  productAccessType: string | undefined;
  licenseStartDate: Date | string | null;
  licenseEndDate: Date | string | null;
  whitelistSuffixes: string[];
  firstName: string;
  lastName: string;
  country: string;
  region: string;
  adminID: string;
  costCenterID: string;
  costCenterName: string | undefined;
  phone: string;
  users: unknown[];
  whitelistIgnore: boolean;
  admins: string[];
  adminUsers: AdminUser[];
  initialAdminUsers: AdminUser[];
  enabledFeatures: Record<string, ProductFeature>;
  // Manage-only / dynamically-assigned fields:
  seatCount?: string;
  maxSharedSeatCount?: string;
  email?: string;
  preEditCostCenterName?: string;
  nextBillingDate?: Date | null;
  newSuffix?: string;
  costCenterOwnerEmail?: string;
  seatsAssigned?: number;
  authorizedDevicesOverride?: boolean;
  shouldCreateUser?: boolean;
  address1?: string;
  address2?: string;
  zipCode?: string;
  // Present in `licenseFields` but never set in state (always undefined at runtime):
  downloadCap?: string;
  storageCap?: string;
}

type StateUpdater =
  | Partial<LicenseModalState>
  | ((prev: LicenseModalState) => Partial<LicenseModalState>);

interface LicenseChange {
  stateKey: string;
  before: unknown;
  after: unknown;
}

interface OriginalValues {
  costCenterID: string;
  activityState: string;
  productAccessID: string;
  type: string;
  region: string;
  maxSharedSeatCount: number;
}

interface FieldValidator {
  validator: (value: string) => boolean;
  getValidationMessage: (value?: string) => string;
}

const licenseFields = {
  productName: 'Product name',
  activityState: 'Activity state',
  productAccessType: 'Product access type',
  licenseStartDate: 'License start date',
  licenseEndDate: 'License end date',
  whitelistSuffixes: 'Whitelist suffixes',
  firstName: 'First name',
  lastName: 'Last name',
  seatCount: 'Seat count',
  maxSharedSeatCount: 'Max Shared Seat Count',
  downloadCap: 'Yearly download limit',
  storageCap: 'Disk space',
  email: 'Primary email',
  costCenterName: 'Cost center',
  country: 'Country',
  phone: 'Phone number',
  whitelistIgnore: 'Bypass Whitelist',
  admins: 'Co-Administrators',
  authorizedDevicesOverride: 'Authorized Devices Override',
  enabledFeatures: 'Product Features',
};

const ProposedSeatsNote = styled.div`
  padding: ${({ theme }) => theme.grid.units(1)};
  font-size: 13px;
  font-style: italic;
  text-align: center;
`;

const ChangesContainer = styled.div`
  margin-top: ${({ theme }) => theme.grid.units(1)};
  max-height: 400px;
  overflow-y: auto;
  padding-right: ${({ theme }) => theme.grid.units(1)};
`;

const ChangeItem = styled.div`
  display: flex;
  margin-bottom: ${({ theme }) => theme.grid.units(1)};
  padding: ${({ theme }) => theme.grid.units(0.5)};

  &:hover {
    background-color: ${({ theme }) => theme.colors.defaultGreyBorderColor};
  }
`;

const ChangeText = styled.span`
  word-wrap: break-word;
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: pre-line;
  flex: 1;
`;

const { Alerts } = FlowjoCommons;

// The wrapped action builders have heterogeneous signatures and are dispatched
// dynamically, so this thin wrapper is a genuine dynamic boundary.
const wrapActionCreator =
  (dispatch: AppDispatch, actionCreator: (...args: any[]) => unknown) => // eslint-disable-line @typescript-eslint/no-explicit-any
    (...args: any[]): void => { // eslint-disable-line @typescript-eslint/no-explicit-any
      dispatch(CloudAdminActions.hideModal());
      const action = actionCreator(...args);
      if (action) {
        dispatch(action as Parameters<AppDispatch>[0]);
      }
    };

const LicenseModal = (): React.JSX.Element => {
  const { confirm } = useContext(LayoutContext);

  const selectedTool = useSelector(selectActiveTool) as string;
  const modals = useSelector(modalsSelector) as { modalType?: string };
  const selectedLicense = useSelector(selectCurrentLicense) as unknown as SelectedLicense | null;
  const costCenterNameCheck = useSelector(selectCostCenterNameCheck) as CostCenterNameCheckState;
  // `selectSeatUsageForProductAccess` internally calls AuthUtils.getSeatUsageForProductAccess and
  // returns its full standardized shape; only `allocatedSeats` was previously typed/used here.
  const seatUsage = useSelector(selectSeatUsageForProductAccess) as unknown as Partial<SeatUsage>;
  const savedProductAccess = useSelector(selectActiveProductAccess) as unknown as
    | Record<string, unknown>
    | undefined;
  // Rows for the name currently being checked, from the live lookup rather than the legacy
  // full-sync dataset (which is empty unless the SuperAdmin opened the legacy view). They are
  // current by submit time because the Wizard's Next/Submit button is gated on the check
  // being current — see isCostCenterNameCheckCurrent).
  const existingLicensesInfo: LicenseData[] = costCenterNameCheck.matchingLicenseRows;

  const dispatch = useAppDispatch();
  const wrap = wrapActionCreator.bind(null, dispatch);
  const hideModal = (): void => {
    dispatch(CloudAdminActions.hideModal());
  };
  const dispatchCreateUserAndLicense = wrap(buildCreateUserAndLicenseAction);
  const dispatchCreateLicense = wrap(buildCreateLicenseAction);
  const dispatchEditLicense = wrap(buildManageLicenseAction);
  const showDuplicateFeaturesAlert = (): void => {
    const alert = Alerts.Alert.AlertStamp({
      id: 'FEATURE_DUPLICATE_WARNING',
      date: null,
      type: 'FEATURE_DUPLICATE_WARNING',
    });
    dispatch(Alerts.AlertActions.setAlert(alert));
  };

  const getInitialState = (): LicenseModalState => {
    const { allocatedSeats } = seatUsage;
    if (modals.modalType === MODAL_TYPES.MANAGE_LICENSE) {
      const { admin, costCenter, productAccess } = selectedLicense!;
      return {
        productName: productAccess.name,
        activityState: productAccess.activityState,
        productAccessType: productAccess.type,
        licenseStartDate: parseDate(productAccess.startDate) || '',
        licenseEndDate: parseDate(productAccess.endDate) || '',
        whitelistSuffixes: costCenter.whitelistDomains,
        firstName: admin.firstname,
        lastName: admin.lastname,
        seatCount: `${productAccess.maxActiveUsers}`,
        maxSharedSeatCount: `${productAccess.maxSharedComputers}`,
        email: admin.primaryEmail,
        preEditCostCenterName: costCenter.name || undefined,
        costCenterName: costCenter.name || '',
        costCenterID: costCenter.id || '',
        nextBillingDate: parseDate(productAccess.endDate),
        newSuffix: '',
        country: costCenter.countryCode,
        phone: '',
        region: productAccess.region,
        adminID: productAccess.owner,
        costCenterOwnerEmail: '',
        whitelistIgnore: costCenter.whitelistIgnore || false,
        admins: costCenter.admins || [],
        adminUsers: [],
        initialAdminUsers: [],
        users: [],
        authorizedDevicesOverride: productAccess.authorizedDevicesOverride || false,
        seatsAssigned: allocatedSeats,
        enabledFeatures: productAccess.features || {},
      };
    }

    return {
      productName: Product.NAMES.flowJo,
      activityState: CostCenter.ACTIVITY_STATES.active,
      productAccessType: RELEASE.productAccessType,
      licenseStartDate: new Date(),
      licenseEndDate: addYears(new Date(), 1),
      whitelistSuffixes: [],
      firstName: '',
      lastName: '',
      country: '',
      region: 'world wide',
      adminID: '',
      costCenterID: '',
      costCenterName: undefined,
      phone: '',
      users: [],
      whitelistIgnore: false,
      admins: [],
      adminUsers: [],
      initialAdminUsers: [],
      enabledFeatures: {},
    };
  };

  // Single object state mirrors the former class `state`; setState merges like
  // React's class setState (object or updater form).
  const [state, setStateRaw] = useState<LicenseModalState>(getInitialState);
  const setState = (update: StateUpdater): void =>
    setStateRaw((prev) => ({
      ...prev,
      ...(typeof update === 'function' ? update(prev) : update),
    }));

  // The seat summary shown beside Max Seats, recomputed with the Max Seats / Max Shared Computers
  // values currently typed into the form so a SuperAdmin sees their effect before saving. Same
  // AuthUtils calculation as the saved-license summary; only the two max values are swapped in. A
  // field that is not a whole number yet (empty, mid-edit) keeps its saved value.
  const proposedSeatUsage = React.useMemo<Partial<SeatUsage>>(() => {
    if (!savedProductAccess || seatUsage.maxSeats === undefined) return seatUsage;
    const toSeatCount = (typed: unknown, saved: unknown): number =>
      /^\d+$/.test(String(typed ?? '').trim()) ? Number(String(typed).trim()) : (saved as number);
    return AuthUtils.getSeatUsageForProductAccess(
      {
        ...savedProductAccess,
        maxActiveUsers: toSeatCount(state.seatCount, savedProductAccess.maxActiveUsers),
        maxSharedComputers: toSeatCount(
          state.maxSharedSeatCount,
          savedProductAccess.maxSharedComputers,
        ),
      },
      seatUsage.pendingUserInvites ?? 0,
    );
  }, [savedProductAccess, seatUsage, state.seatCount, state.maxSharedSeatCount]);
  const seatUsageIsProposed =
    proposedSeatUsage.maxSeats !== seatUsage.maxSeats ||
    proposedSeatUsage.maxShared !== seatUsage.maxShared;

  // Returns unmodified values that `editLicense` handler needs access to:
  const getOriginalValues = (): OriginalValues => {
    const { costCenter, productAccess } = selectedLicense!;

    return {
      costCenterID: costCenter.id,
      activityState: productAccess.activityState,
      productAccessID: productAccess.id,
      type: productAccess.type,
      region: productAccess.region,
      maxSharedSeatCount: productAccess.maxSharedComputers,
    };
  };

  const getChanges = (): LicenseChange[] => {
    const initialState = getInitialState();
    const changes: LicenseChange[] = (Object.keys(licenseFields) as (keyof typeof licenseFields)[])
      .filter((key) => didLicensePropertyChange(key, initialState[key], state[key]))
      .map((key) => ({
        stateKey: key,
        before: initialState[key],
        after: state[key],
      }));
    return changes;
  };

  const validateInstitutionOnlyFields = (): boolean => {
    validateSeatCount.validator(state.seatCount as string);
    const whitelistValid = state.whitelistIgnore ? true : !!state.whitelistSuffixes.length;

    return whitelistValid;
  };

  const validateCommonFields = (): boolean => {
    const validatedSeats = isCreateLicense()
      ? true
      : parseInt(state.seatCount!, 10) >= state.seatsAssigned!;
    const validatedAdminId = isInstitutionLicense() ? validateID.validator(state.adminID) : true;

    return validatedSeats && validatedAdminId;
  };

  const validateCostCenterInfo = (): boolean => {
    const isInstitutional = isInstitutionLicense();
    const validatedCommonFields = validateCommonFields();
    const validatedInstitutionalFields = isInstitutional ? validateInstitutionOnlyFields() : true;
    const validatedCreateLicense = isCreateLicense() ? validateProductAccessNotDuplicate() : true;
    // if not institutional license, the cost center name is defined programatically and doesn't need to be pre-validated
    const validatedCostCenterName = isInstitutional ? alternateCostCenterNameValidator() : true;
    return (
      validatedCommonFields &&
      validatedInstitutionalFields &&
      validatedCreateLicense &&
      validatedCostCenterName
    );
  };

  const validateContactInfo = (): boolean => true;
  // TODO bring these back incrementally as functionality lands. Corresponding inputs are disabled.
  //
  // validateFirstName.validator(state.firstName) &&
  // validateLastName.validator(state.lastName) &&
  // validateAddress.validator(state.address1) &&
  // validateUSZipCode.validator(state.zipCode) &&
  // validateEmail.validator(state.email) &&
  // validatePhone.validator(state.phone);

  // True when the currently-typed name is unchanged from the license's own pre-edit name (i.e.
  // editing an existing license without renaming its cost center) — validateDuplicateCostCenterName
  // already special-cases this internally, but the live check effect below needs to recognize it
  // too, to avoid firing a network request to "check" a name against itself.
  const isCostCenterNameUnchangedFromPreEdit = (name: string | undefined): boolean => {
    const { preEditCostCenterName } = state;
    return (
      !!preEditCostCenterName &&
      convertToStringMatchRelaxed(preEditCostCenterName) === convertToStringMatchRelaxed(name)
    );
  };

  // Debounced (via the saga's own debounce, not here) live lookup — fires on every
  // costCenterName change and lets ADMIN_ELASTICSEARCH_COST_CENTERS_SEARCH settle the question
  // of whether that name (and, per hit, its productAccess) is already in use, replacing the old
  // synchronous checks against selectLicenses (the legacy full-sync dataset, which the new
  // search-first license pages no longer always load — see this ticket's CHANGELOG for the full
  // story). Fires for both license types: alternateCostCenterNameValidator only consults the
  // result for institutional licenses (see validateCostCenterInfo above), but
  // validateProductAccessNotDuplicate consults it for both — an individual license's
  // costCenterName is set programmatically (getExistingIndividualCostCenter, once an owner is
  // picked), not typed, but it's still a real value that check needs live data for.
  useEffect(() => {
    const { costCenterName } = state;
    if (!costCenterName || isCostCenterNameUnchangedFromPreEdit(costCenterName)) return;
    const costCenterType = isInstitutionLicense()
      ? CostCenter.COST_CENTER_TYPES.institutional
      : CostCenter.COST_CENTER_TYPES.individual;
    dispatch(checkCostCenterName(costCenterName, costCenterType));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.costCenterName]);

  // True once costCenterNameCheck holds a settled (non-pending) result for the *currently typed*
  // name — a debounced check for a since-edited name can still resolve after the input moved on,
  // so this guards against trusting a stale result.
  const isCostCenterNameCheckCurrent = (name: string | undefined): boolean =>
    !costCenterNameCheck.pending &&
    costCenterNameCheck.name !== null &&
    convertToStringMatchRelaxed(costCenterNameCheck.name) === convertToStringMatchRelaxed(name);

  const alternateCostCenterNameValidator = (): boolean => {
    // This check assumes the costCenterName value has already been updated in component state
    // This check does not provide the validation message and does not consider the costCenterName beginning in an empty state
    // This check is intended for determining whether or not to allow the superadmin to proceed to the next modal.
    const { preEditCostCenterName, costCenterName } = state;
    const valid = validateCostCenterName.validator(costCenterName);
    if (!costCenterName || isCostCenterNameUnchangedFromPreEdit(costCenterName)) {
      return valid;
    }
    // Block progressing past this step while the live check for the name currently in the
    // field hasn't resolved yet — this is what closes the submit-time race: createLicense()
    // itself never re-validates before dispatching, it only trusts whatever this function last
    // returned via the Wizard's steps[0].valid (see FjWizard, which disables Next/Submit
    // whenever `valid` is false), so "still checking" must count as "not valid yet".
    if (!isCostCenterNameCheckCurrent(costCenterName)) return false;
    const notDuplicate = validateDuplicateCostCenterName(
      preEditCostCenterName,
      costCenterName as string,
      costCenterNameCheck.relaxedNameMatches,
    );
    return valid && notDuplicate;
  };

  const validateCostCenterNameDuplicate = (enteredValue: string): boolean => {
    const { preEditCostCenterName } = state;
    if (!enteredValue || isCostCenterNameUnchangedFromPreEdit(enteredValue)) return true;
    // Unlike alternateCostCenterNameValidator, this drives the input field's own red-border/
    // error-message display on every keystroke — deliberately reporting "valid" (no error
    // shown) while the check is still pending, rather than flashing an error on every
    // keystroke before the debounced check has had a chance to settle. The Wizard's
    // Next/Submit button is what actually gates on the pending state (via
    // alternateCostCenterNameValidator above), so this doesn't weaken the real guard.
    if (!isCostCenterNameCheckCurrent(enteredValue)) return true;
    return validateDuplicateCostCenterName(
      preEditCostCenterName,
      enteredValue,
      costCenterNameCheck.relaxedNameMatches,
    );
  };

  const buildCostCenterNameValidator = (): FieldValidator => {
    const validateNameCollision = {
      validator: (val: unknown): boolean => validateCostCenterNameDuplicate(val as string),
      getValidationMessage: costCenterDuplicateValidationMessage,
    };
    // Bundle it up with name format validator: `validateCostCenterName`
    // `composeValidators` is an untyped JS helper typed via JSDoc as `object`.
    return composeValidators(validateCostCenterName, validateNameCollision) as FieldValidator;
  };

  const validateProductAccessNotDuplicate = (): boolean => {
    // if cost center name is not yet defined, then there is not (yet) a productAccess to analyze
    const { costCenterName } = state;
    if (!costCenterName) return true;
    // Editing an existing license without renaming its cost center needs no check — same
    // short-circuit as alternateCostCenterNameValidator's.
    if (isCostCenterNameUnchangedFromPreEdit(costCenterName)) return true;
    // This function doubles as ProductSelector's own live input validator (see its `validator`
    // prop below) as well as this Wizard step's gate (validateCostCenterInfo above) — unlike
    // alternateCostCenterNameValidator/validateCostCenterNameDuplicate, it isn't split into two
    // variants for that dual use, so a still-pending check briefly reads as "invalid" in both
    // places rather than only blocking Submit. That's an accepted, minor cosmetic trade-off
    // (a brief red flash while the ~400ms debounced check settles) in exchange for keeping this
    // one function closing the same submit-time race alternateCostCenterNameValidator does:
    // createLicense() never re-validates before dispatching, only trusting whatever this
    // function last returned.
    if (!isCostCenterNameCheckCurrent(costCenterName)) return false;
    const duplicateProductAccess = hasMatchingProductNameAndProductAccessType({
      costCenterName,
      productName: state.productName,
      productAccessType: state.productAccessType as string,
      licensesDataArray: existingLicensesInfo,
    });
    if (duplicateProductAccess) {
      return false;
    }
    // otherwise, return true
    return true;
  };

  const handleCostCenterNameChange = (val: string): void => {
    setState({ costCenterName: val });
  };

  const setAdminUsers = async (userIds: string[]): Promise<void> => {
    const { data } = (await fetchUsersByIds(userIds)) as { data: Record<string, AdminUser> };
    const admins = Object.values(data);
    setState({ adminUsers: admins, initialAdminUsers: admins });
  };

  const addAdmin = async (adminId: string): Promise<void> => {
    const { data } = (await fetchUsersByIds([adminId])) as { data: Record<string, AdminUser> };
    const newAdmins = Object.values(data);
    setState(({ admins, adminUsers }) => ({
      admins: [...admins, ...newAdmins.map((admin) => admin.id)],
      adminUsers: [...adminUsers, ...newAdmins],
    }));
  };

  const removeAdmin = (adminId: string): void => {
    setState(({ admins, adminUsers }) => ({
      admins: admins.filter((id) => id !== adminId),
      adminUsers: adminUsers.filter((admin) => admin.id !== adminId),
    }));
  };

  const buildContactInputs = () => [
    {
      Component: Input,
      size: inputSizes.LG,
      placeholder: 'First Name',
      id: 'first-name',
      onChange: (val: string) => setState({ firstName: val }),
      value: state.firstName,
      validator: validateFirstName.validator,
      getValidationMessage: validateFirstName.getValidationMessage,
    },
    {
      Component: Input,
      size: inputSizes.LG,
      placeholder: 'Last Name',
      id: 'last-name',
      onChange: (val: string) => setState({ lastName: val }),
      value: state.lastName,
      validator: validateLastName.validator,
      getValidationMessage: validateLastName.getValidationMessage,
    },
    {
      Component: Input,
      size: inputSizes.LG,
      disabled: true,
      placeholder: 'Address 1',
      id: 'address1',
      onChange: (val: string) => setState({ address1: val }),
      value: state.address1,
      validator: (validateAddress as FieldValidator).validator,
      getValidationMessage: (validateAddress as FieldValidator).getValidationMessage,
    },
    {
      Component: Input,
      size: inputSizes.LG,
      disabled: true,
      placeholder: 'Address 2',
      id: 'address2',
      onChange: (val: string) => setState({ address2: val }),
      value: state.address2,
    },
    {
      Component: Input,
      size: inputSizes.LG,
      disabled: true,
      placeholder: 'Postal Code',
      id: 'postal-code',
      onChange: (val: string) => setState({ zipCode: val }),
      value: state.zipCode,
      validator: validateUSZipCode.validator,
      getValidationMessage: validateUSZipCode.getValidationMessage,
    },
    {
      Component: Input,
      size: inputSizes.LG,
      disabled: true,
      placeholder: 'Phone Number',
      id: 'phone-number',
      onChange: (val: string) => setState({ phone: val }),
      value: state.phone,
      validator: (validatePhone as FieldValidator).validator,
      getValidationMessage: (validatePhone as FieldValidator).getValidationMessage,
    },
  ];

  const isCreateLicense = (): boolean => modals.modalType === MODAL_TYPES.CREATE_NEW_LICENSE;

  const isInstitutionLicense = (): boolean =>
    selectedTool === TOOL_BUTTON_NAMES.INSTITUTION_LICENSE;

  const addSuffixToWhitelist = (suffix: string): void => {
    setState((prevState) => ({
      whitelistSuffixes: prevState.whitelistSuffixes.concat([suffix]),
      newSuffix: '',
    }));
  };

  const removeSuffixFromWhitelist = (suffix: string): void => {
    setState((prevState) => ({
      whitelistSuffixes: prevState.whitelistSuffixes.filter(
        (existingSuffix) => existingSuffix !== suffix,
      ),
    }));
  };

  const hasDuplicateFeatures = (): boolean => {
    const { enabledFeatures } = state;
    const features = Object.values(enabledFeatures || {});
    const seen = new Set<string | undefined>();
    return features.some((f) => {
      if (seen.has(f.productFeatureId)) return true;
      seen.add(f.productFeatureId);
      return false;
    });
  };

  const createLicense = (): void => {
    if (hasDuplicateFeatures()) {
      showDuplicateFeaturesAlert();
      return;
    }
    if (state.shouldCreateUser) {
      dispatchCreateUserAndLicense(state);
    } else {
      dispatchCreateLicense(state, isInstitutionLicense(), existingLicensesInfo);
    }
  };

  const confirmEditLicense = (): void => {
    if (hasDuplicateFeatures()) {
      showDuplicateFeaturesAlert();
      return;
    }
    hideModal();
    const changes = getChanges();

    confirm({
      heading: 'Summary of changes:',
      size: 'xl',
      body: renderChangesConfirmation(changes),
      accept: {
        text: 'Make Changes',
        callback: () => {
          return dispatchEditLicense(changes, {
            ...state,
            original: getOriginalValues(),
          });
        },
      },
    });
  };

  const getExistingIndividualCostCenter = ({
    id,
  }: {
    id?: string;
  }): { costCenterName: string; costCenterID: string } => {
    const individualCostCenterName = CostCenter.getIndividualCostCenterName(id);
    const existingLicenseInfo = getExistingMatch({
      costCenterName: individualCostCenterName,
      licensesDataArray: existingLicensesInfo,
      ownerId: id as string,
    });
    const costCenterId = existingLicenseInfo
      ? (existingLicenseInfo.costCenter as unknown as { id: string }).id
      : '';
    return {
      costCenterName: individualCostCenterName || '',
      costCenterID: costCenterId,
    };
  };

  const getExistingInstitutionalCostCenter = (): {
    costCenterName: string;
    costCenterID: string;
  } => {
    const { costCenterName, costCenterID } = state;
    if (costCenterName && costCenterID) return { costCenterName, costCenterID };
    return { costCenterName: '', costCenterID: '' };
  };

  const getExistingCostCenter = (
    user: AdminTypeaheadMatch,
  ): { costCenterName: string; costCenterID: string } =>
    isInstitutionLicense()
      ? getExistingInstitutionalCostCenter()
      : getExistingIndividualCostCenter(user);

  const handleAdminTypeaheadChange = (match: AdminTypeaheadMatch | undefined): void => {
    // The email typeahead always yields a selected item before this fires.
    const userMatch = match as AdminTypeaheadMatch;
    const shouldCreateUser = userMatch.id === 'CREATE';
    const cc = getExistingCostCenter(userMatch);
    setState({
      adminID: userMatch.id || '',
      email: userMatch.primaryEmail || '',
      firstName: userMatch.firstname || '',
      lastName: userMatch.lastname || '',
      country: userMatch.countryCode || '',
      costCenterName: cc.costCenterName || '',
      costCenterID: cc.costCenterID || '',
      shouldCreateUser,
    });
  };

  const renderChangesConfirmation = (changes: LicenseChange[] = []): React.JSX.Element => {
    return (
      <ChangesContainer>
        {changes.map((change) => {
          let { before, after } = change;

          const prettierPrintArray = (array: unknown[]): string =>
            array.length ? prettyPrintArray(array) : 'none';
          const prettyPrintBool = (bool: unknown): string => (bool ? 'true' : 'false');
          const beforeOrAfter = (func: (value: unknown) => unknown): unknown =>
            func(before) || func(after);

          if (change.stateKey === 'admins') {
            before = state.initialAdminUsers.map((user) => user.primaryEmail);
            after = state.adminUsers.map((user) => user.primaryEmail);
          }

          if (change.stateKey === 'enabledFeatures') {
            const formatFeatures = (features: unknown): string => {
              const enabledList = formatEnabledFeatures(features);
              return enabledList.length ? `\n  ${enabledList.join('\n  ')}` : 'none';
            };
            before = formatFeatures(before);
            after = formatFeatures(after);
          }

          // Format changes:
          if (beforeOrAfter((value) => value instanceof Date)) {
            before = formatDate(before as Date);
            after = formatDate(after as Date);
          } else if (beforeOrAfter(Array.isArray)) {
            before = prettierPrintArray(before as unknown[]);
            after = prettierPrintArray(after as unknown[]);
          } else if (beforeOrAfter((value) => typeof value === 'boolean')) {
            before = prettyPrintBool(before);
            after = prettyPrintBool(after);
          }

          return (
            <ChangeItem key={change.stateKey}>
              <ChangeText>
                {licenseFields[change.stateKey as keyof typeof licenseFields]}:&ensp;
                {before as React.ReactNode}&nbsp;&rarr;&nbsp;{after as React.ReactNode}
              </ChangeText>
            </ChangeItem>
          );
        })}
      </ChangesContainer>
    );
  };

  const renderTitle = (_step?: number): string => {
    // (step) {
    const task = isCreateLicense() ? 'Create New' : 'Manage';
    const type = isInstitutionLicense() ? 'Institution' : 'Individual';
    const product = isCreateLicense()
      ? ''
      : `– ${Product.PRODUCT_DISPLAY_NAMES[state.productName]}`;

    return `${task} ${type} License ${product}`; // ${stepTask} ${addendum}`;
  };

  const renderProductSelector = (): React.JSX.Element | null => {
    if (isCreateLicense()) {
      const prevalidationMessage = ` Cost center with matching product type and license type exists`;

      return (
        <ProductSelector
          value={state.productName}
          onChange={(val) => setState({ productName: val as string })}
          validator={validateProductAccessNotDuplicate}
          validatorMessage={prevalidationMessage}
        />
      );
    }
    return null;
  };

  const renderProductAccessTypeSelector = (): React.JSX.Element => {
    const radios = [RELEASE, TRIAL, TRAINING, BETA];

    const selectedLicense = radios.find(
      (radio) => radio.productAccessType === state.productAccessType,
    );

    return (
      <ModalRadioGroup
        title="License Type"
        name="license"
        selected={selectedLicense}
        radios={radios}
        className="margin-right-1"
        onChange={(val) => setState({ productAccessType: (val as LicenseRadioOption)?.productAccessType })}
      />
    );
  };

  const renderActivityStateSelector = (): React.JSX.Element | null => {
    const radios = [PENDING, ACTIVE, CANCELLED, SUSPENDED];
    const selectedLicense = radios.find((radio) => radio.activityState === state.activityState);
    if (isCreateLicense()) {
      return null;
    }
    return (
      <ModalRadioGroup
        title="Activity State"
        name="license-activity-state"
        selected={selectedLicense}
        radios={radios}
        className="margin-right-1"
        onChange={(val) => setState({ activityState: (val as LicenseRadioOption)?.activityState })}
      />
    );
  };

  const renderWizardCostCenterInfoLeft = (): React.JSX.Element => {
    return (
      <div className="display-flex flex-column">
        <EmailLookup
          sectionTitle="License Administrator"
          initialValue={state.email}
          onSelect={handleAdminTypeaheadChange}
          showPhantomUsers={!isInstitutionLicense()}
          allowInvite={!isInstitutionLicense()}
        />
        <CountryCode value={state.country} onChange={(val) => setState({ country: val })} />
        {isInstitutionLicense() && (
          <>
            <ModalInputSection
              inputs={[
                {
                  Component: Input,
                  placeholder: 'Cost Center Name',
                  id: 'cost-center-name',
                  onChange: handleCostCenterNameChange,
                  value: state.costCenterName,
                  validator: buildCostCenterNameValidator().validator,
                  getValidationMessage: buildCostCenterNameValidator().getValidationMessage,
                  validateImmediate: true,
                },
              ]}
              sectionTitle="Cost Center Name"
            />
            <EmailLookup
              sectionTitle="Co-Administrators"
              resetValue
              onSelect={(user) => addAdmin(user!.id!)}
            />
            <CoAdminList
              listRecords={state.adminUsers as unknown as Array<Record<string, React.ReactNode>>}
              onDelete={(user) => removeAdmin((user as unknown as AdminUser).id)}
            />
          </>
        )}
      </div>
    );
  };

  const renderWizardCostCenterInfoRight = (): React.JSX.Element => {
    return (
      <div className="display-flex flex-column">
        <BillingPeriodManager
          licenseStartDate={state.licenseStartDate as Date | null}
          licenseEndDate={(state.licenseEndDate || null) as Date | null}
          onStartDateChange={(e) => setState({ licenseStartDate: e })}
          onEndDateChange={(e) => setState({ licenseEndDate: e })}
        />
        {isInstitutionLicense() && (
          <SeatStorageManager
            onChange={(e) => setState({ seatCount: e as unknown as string })}
            seatCount={state.seatCount}
            usedSeats={state.seatsAssigned}
            isCreateLicense={isCreateLicense()}
            // Only meaningful once a real license's seat usage has been computed - never during
            // license creation, and never before selectSeatUsageForProductAccess has a match
            // (its early-return branches yield {}, per that selector's own comment).
            seatSummary={
              !isCreateLicense() && proposedSeatUsage.maxSeats !== undefined ? (
                <>
                  <SeatSummary
                    name={PRODUCT_INFO[state.productName]?.name ?? state.productName}
                    Icon={PRODUCT_INFO[state.productName]?.Icon ?? null}
                    seats={proposedSeatUsage as SeatUsage}
                    productName={state.productName}
                  />
                  {seatUsageIsProposed && (
                    <ProposedSeatsNote>
                      Reflects the Max Seats / Max Shared Computers values entered above, not yet
                      saved.
                    </ProposedSeatsNote>
                  )}
                </>
              ) : undefined
            }
          />
        )}
        {isInstitutionLicense() && (
          <SharedMaxSeatStorageManager
            onChange={(e) => setState({ maxSharedSeatCount: e })}
            sharedSeatCount={state.maxSharedSeatCount}
          />
        )}
        {isInstitutionLicense() && (
          <EmailSuffixManager
            value={state.newSuffix as string}
            onCheckboxChange={(e) => setState({ whitelistIgnore: e.target.checked })}
            onSuffixChange={(e) =>
              // `EmailSuffixManager` types this callback as receiving a change event, but the
              // underlying styled `Input` emits the raw string value at runtime.
              setState({ newSuffix: prefixEmailDomain(e as unknown as string) })
            }
            addSuffixToWhitelist={addSuffixToWhitelist}
            removeSuffixFromWhitelist={removeSuffixFromWhitelist}
            whitelistSuffixes={state.whitelistSuffixes}
            whitelistIgnore={state.whitelistIgnore}
          />
        )}
        <AuthorizedDevicesOverride
          checkboxState={state.authorizedDevicesOverride}
          onChange={(e) => setState({ authorizedDevicesOverride: e.target.checked })}
          isCreateLicense={isCreateLicense()}
        />
        <ProductFeaturesManager
          productName={state.productName}
          // Local `ProductFeature` is a loose superset of the manager's stricter
          // `ProductAccessFeature`; the runtime data carries the required fields.
          enabledFeatures={
            state.enabledFeatures as unknown as React.ComponentProps<
              typeof ProductFeaturesManager
            >['enabledFeatures']
          }
          onChange={(features: Record<string, ProductFeature>) =>
            setState({ enabledFeatures: features })
          }
        />
      </div>
    );
  };

  const renderWizardCostCenterInfo = (): React.JSX.Element => {
    return (
      <FjFlexContainer>
        <WizardInnerPane>
          {isCreateLicense() && renderProductSelector()}
          {renderActivityStateSelector()}
          {renderProductAccessTypeSelector()}
          {renderWizardCostCenterInfoLeft()}
        </WizardInnerPane>
        <WizardInnerPane>{renderWizardCostCenterInfoRight()}</WizardInnerPane>
      </FjFlexContainer>
    );
  };

  const renderWizardContactInfo = (): React.JSX.Element => {
    return (
      <div className="display-flex flex-column">
        <div className="display-flex margin-top-sm" style={{ fontSize: '14px' }}>
          <div className="display-flex flex-column padding-right-med border-box">
            <ModalInputSection
              inputs={buildContactInputs().filter(Boolean)}
              sectionTitle="Add Contact Details"
            />
          </div>
        </div>
      </div>
    );
  };

  // componentDidMount: load admin users for an existing license.
  useEffect(() => {
    if (!selectedLicense) return;
    setAdminUsers(selectedLicense.costCenter.admins || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const steps = [
    {
      component: renderWizardCostCenterInfo(),
      valid: validateCostCenterInfo(),
      title: renderTitle(0),
    },
    {
      component: renderWizardContactInfo(),
      valid: validateContactInfo(),
      title: renderTitle(1),
    },
  ];
  return (
    <Wizard
      windowSize={fjModalSizes.XXL}
      steps={steps}
      onSubmit={isCreateLicense() ? createLicense : confirmEditLicense}
      showX
      onClickX={hideModal}
      allowScroll
    />
  );
};

export default LicenseModal;
